import { useEffect, useRef } from "react";

const GOLD_CORE = "#f5d08a";
const GOLD_RIM = "#c4956a";
const GOLD_DEEP = "#c9a02f";

export default function VoiceWaveform({ analyserRef, active, isSpeaking }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const amplitudeRef = useRef(0);
  const phaseRef = useRef(0);
  const ripplesRef = useRef([]);
  const lastRippleAtRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(canvas);
    } else {
      window.addEventListener("resize", resize);
    }

    const timeData = new Uint8Array(1024);

    const readAmplitude = () => {
      const analyser = analyserRef?.current;
      if (!analyser) return 0;
      try {
        analyser.getByteTimeDomainData(timeData);
      } catch (_) {
        return 0;
      }
      const len = Math.min(timeData.length, analyser.fftSize || timeData.length);
      let sumSq = 0;
      for (let i = 0; i < len; i++) {
        const v = (timeData[i] - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / len);
      return Math.min(1, rms * 2.4);
    };

    const draw = (now) => {
      if (!reducedMotion) {
        rafRef.current = requestAnimationFrame(draw);
      }

      const raw = readAmplitude();
      const smoothing = raw > amplitudeRef.current ? 0.35 : 0.08;
      amplitudeRef.current += (raw - amplitudeRef.current) * smoothing;

      phaseRef.current += reducedMotion ? 0 : 0.016;

      ctx.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const minSide = Math.min(width, height);
      const breath = 0.5 + 0.5 * Math.sin(phaseRef.current * 0.7);
      const idleScale = 0.04 + breath * 0.03;
      const amp = amplitudeRef.current;
      const coreR = minSide * (0.16 + idleScale + amp * 0.22);

      const speakingBoost = isSpeaking ? 1 : 0.72;

      if (isSpeaking && amp > 0.18 && now - lastRippleAtRef.current > 180) {
        ripplesRef.current.push({
          born: now,
          strength: Math.min(1, amp * 1.3),
        });
        lastRippleAtRef.current = now;
      }

      const RIPPLE_LIFE = 1800;
      ripplesRef.current = ripplesRef.current.filter(
        (r) => now - r.born < RIPPLE_LIFE,
      );

      for (const r of ripplesRef.current) {
        const t = (now - r.born) / RIPPLE_LIFE;
        const radius = coreR + t * minSide * 0.42;
        const alpha = (1 - t) * 0.18 * r.strength;
        if (alpha <= 0.005) continue;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(196, 149, 106, ${alpha.toFixed(3)})`;
        ctx.lineWidth = 1 + (1 - t) * 1.2;
        ctx.stroke();
      }

      const outerR = coreR * 2.2;
      const outerGrad = ctx.createRadialGradient(cx, cy, coreR * 0.4, cx, cy, outerR);
      outerGrad.addColorStop(0, `rgba(182, 122, 26, ${(0.18 + amp * 0.2) * speakingBoost})`);
      outerGrad.addColorStop(0.5, `rgba(122, 36, 89, ${(0.08 + amp * 0.1) * speakingBoost})`);
      outerGrad.addColorStop(1, "rgba(196, 149, 106, 0)");
      ctx.fillStyle = outerGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
      ctx.fill();

      const wavePoints = 96;
      const waveR = coreR * (1.05 + amp * 0.25);
      const waveAmp = minSide * (0.012 + amp * 0.065);
      ctx.beginPath();
      for (let i = 0; i <= wavePoints; i++) {
        const theta = (i / wavePoints) * Math.PI * 2;
        const wobble =
          Math.sin(theta * 3 + phaseRef.current * 1.6) * waveAmp * 0.6 +
          Math.sin(theta * 5 - phaseRef.current * 1.1) * waveAmp * 0.4;
        const r = waveR + wobble;
        const x = cx + Math.cos(theta) * r;
        const y = cy + Math.sin(theta) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(122, 36, 89, ${(0.58 + amp * 0.25) * speakingBoost})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      coreGrad.addColorStop(0, `rgba(246, 215, 146, ${0.62 * speakingBoost})`);
      coreGrad.addColorStop(0.55, `rgba(182, 122, 26, ${0.32 * speakingBoost})`);
      coreGrad.addColorStop(1, "rgba(182, 122, 26, 0)");
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();
    };

    if (active) {
      if (reducedMotion) draw(performance.now());
      else rafRef.current = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", resize);
      }
    };
  }, [active, isSpeaking, analyserRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        width: 240,
        height: 240,
        display: "block",
        background: "transparent",
        mixBlendMode: "normal",
        WebkitMaskImage:
          "radial-gradient(circle, rgba(0,0,0,1) 42%, rgba(0,0,0,0) 72%)",
        maskImage:
          "radial-gradient(circle, rgba(0,0,0,1) 42%, rgba(0,0,0,0) 72%)",
        pointerEvents: "none",
      }}
    />
  );
}

export { GOLD_CORE, GOLD_RIM, GOLD_DEEP };
